import { useCallback, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { DateTime } from "luxon";
import classNames from "classnames";
import { USER_ROLES, userFullName } from "../../utils/userUtils";

import useGlobalParams from "../../hooks/useGlobalParams";
import useUsers from "../../hooks/useUsers"
import useUserRoles from "../../hooks/useUserRoles";

import { Trans as T, useTranslation } from 'react-i18next';
import PLink from "../../common/Navigation/PLink";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import ArrowIcon from "../../assets/icons/ArrowIcon";
import { ReactComponent as SearchIcon } from "../../assets/icons_svg/search-icon.svg";
import { ReactComponent as ErrorIcon } from "../../assets/icons_svg/error-lined.svg";

import './Users.scss';

const COLUMNS = {
  name       : "Name",
  email      : "Email",
  role       : "Role",
  date_added : "Date Added",
}

const getFullName = (user, role) => (
  user ? userFullName(user) : role.invitation_email?.split('@')[0]
);

// lowercase, trim whitespace and convert any multiple whitespaces to single ones,
// normalize to replace diacritics and accents such as ñ -> n and á -> a
const normalizeString = string => (
  string.toLowerCase().trim().replace(/\s+/g, ' ').normalize("NFD").replace(/[\u0300-\u036f]/g, '')
);

export default function Users() {  
  const [sortBy, setSortBy] = useState('date_added');
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [searchRoles, setSearchRoles] = useState(null);
  
  const { organizationId } = useGlobalParams();
  const { t } = useTranslation();
  
  const { data: users } = useUsers({ organizationId });
  const { data: userRoles } = useUserRoles({ organizationId });

  const getUser = useCallback(role => {
    return users.find(user => user.id === role.user_id);
  }, [users]);

  const onChangeSearch = value => {
    setSearch(value);
    
    const text = normalizeString(value);
    debouncedSearch(text);
    text.length === 0 && debouncedSearch.flush();
  }

  const debouncedSearch = useDebouncedCallback(text => {
    if (!text) {
      setSearchRoles(null);
    }
    else {
      // NOTE: this is a shallow copy of userRoles
      setSearchRoles(userRoles.filter(role => {
        const user = getUser(role);

        const fullName = normalizeString(getFullName(user, role));
        const email = user?.email || role.invitation_email;
        
        return fullName.includes(text) || email.includes(text);
      })); 
    }
  }, 300, { maxWait: 1000 }); 

  const sortedRoles = useMemo(() => {
    const orderDirection = (sortDir === 'asc' ? 1 : -1);

    switch (sortBy) {
      case 'date_added':
        return sortDir === 'asc' ? userRoles : userRoles.toReversed();

      case 'name':
        return userRoles.sort((roleA, roleB) => {
          const nameA = getFullName(getUser(roleA), roleA);
          const nameB = getFullName(getUser(roleB), roleB);

          return nameA.localeCompare(nameB) * orderDirection;
        });

      case 'email': {
        return userRoles.sort((roleA, roleB) => {
          const userA = getUser(roleA);
          const userB = getUser(roleB);

          const emailA = userA?.email || roleA.invitation_email;
          const emailB = userB?.email || roleB.invitation_email;

          return emailA.localeCompare(emailB) * orderDirection;
        });
      }

      case 'role': {
        return userRoles.sort((roleA, roleB) => {
          const roleTypeA = Object.keys(USER_ROLES).findIndex(role => role === roleA.type);
          const roleTypeB = Object.keys(USER_ROLES).findIndex(role => role === roleB.type);

          return (roleTypeA - roleTypeB) * orderDirection;
        });
      }

      // pending invitations are shown in the 'date added' column:
      // case 'status': {
      //   return userRoles.sort((roleA, roleB) => {
      //     const inviteA = roleA.user_id ? 1 : 0;
      //     const inviteB = roleB.user_id ? 1 : 0;

      //     return (inviteA - inviteB) * orderDirection;
      //   });
      // }

      default:
        return userRoles;
    }

  }, [getUser, userRoles, sortBy, sortDir]);

  const renderRoleTooltip = (props, role) => (
    <Tooltip {...props} className="__tooltip">{ t(`${role}-role-desc`)}</Tooltip>
  );

  const renderDatePending = role => {
    if (role.user_id) {
      return DateTime.fromISO(role.date_added).toLocaleString(DateTime.DATE_MED);
    }
    
    const expiryDate = DateTime.fromISO(role.invitation_expiration);

    if (DateTime.now().startOf('day') > expiryDate.startOf('day')) {
      return <div className="invitation-expired"><ErrorIcon /><span><T>Invitation Expired</T></span></div>;
    }
    else {
      return <T>Invitation Pending</T>;
    }
  }

  const renderRow = (role, user) => (
    <PLink className='user-row' to={`/user-roles/${role.id}`} key={role.id}>
      <div className="name">{ getFullName(user, role) }</div>
      <div className="email">{ user?.email || role.invitation_email }</div>
      <div className="role">
        <OverlayTrigger placement="bottom" delay={{ show: 300, hide: 300 }} overlay={props => renderRoleTooltip(props, role.type)}>
          <div className="role-description"><T>{ USER_ROLES[role.type] }</T></div>
        </OverlayTrigger>
      </div>
      <div className={classNames("date_added", {"invitation-pending": !role.user_id })}>
        { renderDatePending(role) }
      </div>
    </PLink>
  );

  const changeSort = by => {
    if (by === sortBy) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    }
    else {
      setSortBy(by);
      setSortDir('asc');
    }
  }

  const renderNoMatches = () => (
    <div className="no-matches">
      <T>No users match your search. <button onClick={() => onChangeSearch('')}>Clear search</button></T>
    </div>
  )
  
  const showLoading = !users || !userRoles;

  const renderSkeletonRow = () => (
    <div className='skeleton-row'>
      <div className="name"><div className="skeleton-field" /></div>
      <div className="email"><div className="skeleton-field" /></div>
      <div className="role"><div className="skeleton-field" /></div>
      <div className="date_added"><div className="skeleton-field" /></div>
    </div>
  )

  const renderLoading = () => (
    <>
      { renderSkeletonRow() }
      { renderSkeletonRow() }
      { renderSkeletonRow() }
    </>
  );

  const renderContent = () => (
    <div className="content">
      <div className="match-clear">
        { searchRoles?.length > 0
        ? <>Showing {searchRoles.length} out of {userRoles.length} { userRoles.length === 1 ? 'user' : 'users'}. <button onClick={() => onChangeSearch('')}>Clear search</button></>
        : '\u00A0'
        }
      </div>
      <div className="users-table">
        <div className="table-head">
          { Object.entries(COLUMNS).map(([key, value]) => (
            <div key={key} className={key}>
              <button className={classNames("sort-by", { selected: key === sortBy })} onClick={() => changeSort(key)}>
                <span><T>{ value }</T></span>
                { key === sortBy && <span className='arrow'><ArrowIcon className={classNames(sortDir)} /></span> }
              </button>
            </div>
          ))}
        </div>
        { showLoading
        ? renderLoading()
        : (searchRoles || sortedRoles).map(role => renderRow(role, users.find(user => user.id === role.user_id)))
        }
      </div>
    </div>
  );

  return (
    <section className="__users">
      <div className="title-actions">
        <h2><T>Users & Roles</T></h2>

        <div className="search">
          <SearchIcon />
          <input type="text" placeholder={t("Search by name or email")} autoCorrect="false" autoCapitalize="off" autoComplete="false" spellCheck="false" value={search} onChange={e => onChangeSearch(e.target.value)} />
          { search.length > 0 && <button onClick={() => onChangeSearch('')} className="clear">×</button> }
        </div>

        <PLink to='/users/invite' className="add-user"><T>＋ Add User</T></PLink>
      </div>

      { searchRoles?.length === 0 ? renderNoMatches() : renderContent() }
    </section>
  )
}
